var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useRef, useEffect, useCallback } from "react";
import { uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import { Button, CircularProgress, Box, Typography, Paper, IconButton, Grid, Chip } from "@mui/material";
import { styled } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ArticleIcon from "@mui/icons-material/Article";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import { CloudUpload as CloudUploadIcon } from "@mui/icons-material";
import { useNotification } from "../../context/NotificationContext";
const Input = styled("input")({
    display: "none",
});
export const SharedFileAttachmentUploader = ({ requestId, onUploadComplete, onError, onUploadStatusChange, maxFiles = 10, autoUpdate = true, acceptedFileTypes = "image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv", currentAuthUser, getref, getStoragePath, }) => {
    const [uploads, setUploads] = useState([]);
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);
    const prevCompletedAttachmentsRef = useRef([]);
    const { showNotification } = useNotification();
    const [isDragging, setIsDragging] = useState(false);
    // Report upload status changes to parent
    useEffect(() => {
        const isUploading = uploads.some(upload => upload.isUploading);
        if (onUploadStatusChange) {
            onUploadStatusChange(isUploading);
        }
    }, [uploads, onUploadStatusChange]);
    // Function to convert UploadStatus to Attachment
    const convertToAttachment = useCallback((upload) => {
        return {
            url: upload.url,
            fileName: upload.fileName,
            fileType: upload.fileType,
            uploadedAt: upload.uploadedAt,
        };
    }, []);
    // Update completed attachments when uploads change - ensure autoUpdate works
    useEffect(() => {
        // Skip the effect if autoUpdate is disabled
        if (!autoUpdate)
            return;
        const completedAttachments = uploads.filter(upload => !upload.isUploading && upload.url).map(convertToAttachment);
        // Only update if we have completed attachments and they've changed
        if (completedAttachments.length > 0 &&
            JSON.stringify(prevCompletedAttachmentsRef.current) !== JSON.stringify(completedAttachments)) {
            onUploadComplete(completedAttachments);
            prevCompletedAttachmentsRef.current = completedAttachments;
        }
    }, [uploads, onUploadComplete, autoUpdate, convertToAttachment]);
    const getFileTypeIcon = (fileType) => {
        if (fileType.startsWith("image/"))
            return _jsx(ImageIcon, {});
        if (fileType.startsWith("video/"))
            return _jsx(VideoFileIcon, {});
        if (fileType === "application/pdf")
            return _jsx(PictureAsPdfIcon, {});
        if (fileType.includes("document") || fileType.includes("text"))
            return _jsx(ArticleIcon, {});
        return _jsx(InsertDriveFileIcon, {});
    };
    const getFileTypeLabel = (fileType) => {
        var _a;
        if (fileType.startsWith("image/"))
            return fileType.split("/")[1].toUpperCase();
        if (fileType.startsWith("video/"))
            return fileType.split("/")[1].toUpperCase();
        if (fileType === "application/pdf")
            return "PDF";
        if (fileType.includes("document"))
            return "DOC";
        if (fileType.includes("text"))
            return "TXT";
        return ((_a = fileType.split("/").pop()) === null || _a === void 0 ? void 0 : _a.toUpperCase()) || "FILE";
    };
    const uploadFile = (file) => __awaiter(void 0, void 0, void 0, function* () {
        // Check if user is authenticated
        const currentUser = currentAuthUser;
        if (!currentUser) {
            console.error("User is not authenticated");
            setError("You must be logged in to upload files");
            onError(new Error("User not authenticated"));
            return;
        }
        // Check if we already reached the max number of files
        if (uploads.length >= maxFiles) {
            setError(`Maximum of ${maxFiles} files allowed`);
            onError(new Error(`Maximum of ${maxFiles} files allowed`));
            return;
        }
        // Create a unique ID for this upload
        const uploadId = uuidv4();
        const now = new Date();
        // Add a new upload to the state
        setUploads(prev => [
            ...prev,
            {
                id: uploadId,
                file,
                progress: 0,
                url: "",
                isUploading: true,
                error: null,
                fileType: file.type || "application/octet-stream",
                fileName: file.name,
                uploadedAt: now,
            },
        ]);
        try {
            // Validate file type
            const validFileTypes = [
                "image/",
                "video/",
                "application/pdf",
                "application/msword",
                "application/vnd.openxmlformats-officedocument",
                "text/plain",
                "text/csv",
            ];
            const isValidType = validFileTypes.some(type => file.type.includes(type));
            if (!isValidType) {
                throw new Error(`File type not supported. Allowed types: images, videos, PDF, Word, Excel, PowerPoint, text files and CSV.`);
            }
            // Validate file size (10MB max)
            const MAX_SIZE = 10 * 1024 * 1024; // 10MB
            if (file.size > MAX_SIZE) {
                throw new Error("File size exceeds 10MB limit");
            }
            // Create a unique filename
            const fileExtension = file.name.split(".").pop() || "";
            const fileName = `${uuidv4()}.${fileExtension}`;
            // Ensure file type is properly tracked
            const fileType = file.type || "application/octet-stream";
            //should be a passed function
            const storagePath = getStoragePath(requestId, fileName);
            const storageRef = getref(storagePath);
            // Add metadata including user ID and file type
            const metadata = {
                contentType: fileType,
                customMetadata: {
                    uploadedBy: currentUser.uid,
                    uploadedAt: now.toISOString(),
                    originalFilename: file.name,
                    fileType: fileType,
                },
            };
            // Upload file with progress tracking
            const uploadTask = uploadBytesResumable(storageRef, file, metadata);
            uploadTask.on("state_changed", snapshot => {
                // Track progress
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setUploads(prev => prev.map(upload => (upload.id === uploadId ? Object.assign(Object.assign({}, upload), { progress }) : upload)));
            }, error => {
                // Handle error
                console.error("Upload error:", error);
                const errorMessage = error.message || "Upload failed";
                setUploads(prev => prev.map(upload => (upload.id === uploadId ? Object.assign(Object.assign({}, upload), { error: errorMessage, isUploading: false }) : upload)));
                setError(`Error: ${errorMessage}`);
                onError(error);
            }, () => __awaiter(void 0, void 0, void 0, function* () {
                // Upload completed successfully
                try {
                    const downloadUrl = yield getDownloadURL(uploadTask.snapshot.ref);
                    // Update the state for this upload
                    setUploads(prev => {
                        // Create a new array with this upload marked as completed
                        const updatedUploads = prev.map(upload => upload.id === uploadId
                            ? Object.assign(Object.assign({}, upload), { url: downloadUrl, isUploading: false, progress: 100 }) : upload);
                        // Calculate all completed uploads from the updated array
                        const completedAttachments = updatedUploads
                            .filter(upload => !upload.isUploading && upload.url)
                            .map(convertToAttachment);
                        // Notify parent with ALL completed uploads
                        if (completedAttachments.length > 0) {
                            // Use setTimeout to prevent React state update conflicts
                            setTimeout(() => {
                                onUploadComplete(completedAttachments);
                                prevCompletedAttachmentsRef.current = completedAttachments;
                            }, 10);
                        }
                        return updatedUploads;
                    });
                }
                catch (err) {
                    console.error("Error getting download URL:", err);
                    const errorMessage = err instanceof Error ? err.message : "Failed to get download URL";
                    setUploads(prev => prev.map(upload => upload.id === uploadId ? Object.assign(Object.assign({}, upload), { error: errorMessage, isUploading: false }) : upload));
                    setError(`Error: ${errorMessage}`);
                    onError(err instanceof Error ? err : new Error(errorMessage));
                }
            }));
        }
        catch (error) {
            console.error("Upload preparation error:", error);
            const errorMessage = error instanceof Error ? error.message : "Unknown error";
            // Update the error for this specific upload
            setUploads(prev => prev.filter(upload => upload.id !== uploadId));
            setError(`Error: ${errorMessage}`);
            onError(error instanceof Error ? error : new Error("Unknown upload error"));
        }
    });
    const handleFileSelect = (event) => {
        if (event.target.files && event.target.files.length > 0) {
            setError(null);
            // Convert FileList to array for easier processing
            const filesToUpload = Array.from(event.target.files);
            // Check if adding these files would exceed the max limit
            if (uploads.length + filesToUpload.length > maxFiles) {
                const remainingSlots = Math.max(0, maxFiles - uploads.length);
                setError(`Can only upload ${remainingSlots} more file${remainingSlots !== 1 ? "s" : ""}. 
                   Selected ${filesToUpload.length} file${filesToUpload.length !== 1 ? "s" : ""}.`);
                // If some files can still be uploaded, take only what we can
                if (remainingSlots > 0) {
                    filesToUpload.slice(0, remainingSlots).forEach(file => {
                        uploadFile(file);
                    });
                }
                return;
            }
            // Upload each file
            filesToUpload.forEach(file => {
                uploadFile(file);
            });
        }
    };
    const handleRemoveFile = (id) => {
        // Find the upload to get its URL
        const upload = uploads.find(u => u.id === id);
        if (upload && upload.url) {
            // Try to delete from storage
            try {
                // Parse the storage path from the URL
                const storageRef = getref(upload.url);
                deleteObject(storageRef).catch(err => {
                    console.error("Error deleting file from storage:", err);
                });
            }
            catch (err) {
                console.error("Error parsing storage path:", err);
            }
        }
        // Remove from state and notify parent
        setUploads(prev => {
            // Filter out the removed file
            const updatedUploads = prev.filter(upload => upload.id !== id);
            // Get all completed uploads
            const completedAttachments = updatedUploads
                .filter(upload => !upload.isUploading && upload.url)
                .map(convertToAttachment);
            // Notify parent with updated attachments list
            setTimeout(() => {
                onUploadComplete(completedAttachments);
                prevCompletedAttachmentsRef.current = completedAttachments;
            }, 10);
            return updatedUploads;
        });
    };
    // Show error notification when there's an error
    const prevErrorRef = useRef(null);
    useEffect(() => {
        if (error && error !== prevErrorRef.current) {
            prevErrorRef.current = error;
            showNotification({
                message: error,
                severity: "error",
            });
            // Also call the onError callback
            onError(new Error(error));
        }
    }, [error, showNotification, onError]);
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };
    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };
    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            setError(null);
            // Convert FileList to array for easier processing
            const filesToUpload = Array.from(e.dataTransfer.files);
            // Check if adding these files would exceed the max limit
            if (uploads.length + filesToUpload.length > maxFiles) {
                const remainingSlots = Math.max(0, maxFiles - uploads.length);
                setError(`Can only upload ${remainingSlots} more file${remainingSlots !== 1 ? "s" : ""}. 
                   Selected ${filesToUpload.length} file${filesToUpload.length !== 1 ? "s" : ""}.`);
                // If some files can still be uploaded, take only what we can
                if (remainingSlots > 0) {
                    filesToUpload.slice(0, remainingSlots).forEach(file => {
                        uploadFile(file);
                    });
                }
                return;
            }
            // Upload each file
            filesToUpload.forEach(file => {
                uploadFile(file);
            });
        }
    };
    return (_jsxs(Box, { onDragOver: handleDragOver, onDragLeave: handleDragLeave, onDrop: handleDrop, sx: {
            position: "relative",
            borderRadius: "12px",
            overflow: "hidden",
            transition: "all 0.2s ease-in-out",
            "&::after": isDragging
                ? {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0, 171, 155, 0.08)",
                    border: "2px dashed rgba(0, 171, 155, 0.5)",
                    borderRadius: "12px",
                    zIndex: 1,
                    pointerEvents: "none",
                }
                : {},
        }, children: [uploads.length > 0 ? (_jsx(Grid, { container: true, spacing: 2, children: uploads.map(upload => (_jsx(Grid, { item: true, xs: 6, sm: 4, md: 3, sx: { minWidth: '140px' }, children: _jsx(Paper, { elevation: 2, sx: {
                            p: 1.5,
                            position: "relative",
                            borderRadius: "10px",
                            overflow: "hidden",
                            height: "100%",
                            minHeight: 120,
                            minWidth: '100%',
                            display: "flex",
                            flexDirection: "column",
                            transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                            "&:hover": {
                                transform: "translateY(-2px)",
                                boxShadow: 3,
                            },
                        }, children: upload.isUploading ? (_jsxs(Box, { sx: {
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                height: "100%",
                                p: 2,
                            }, children: [_jsx(CircularProgress, { size: 28, sx: { color: "#00AB9B", mb: 1 } }), _jsxs(Typography, { variant: "body2", sx: { color: "rgba(16, 29, 59, 0.8)" }, children: [Math.round(upload.progress), "%"] })] })) : (_jsxs(_Fragment, { children: [_jsxs(Box, { sx: {
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        p: 1.5,
                                        mb: 1,
                                        backgroundColor: "rgba(16, 29, 59, 0.03)",
                                        borderRadius: "8px",
                                        minHeight: 70,
                                    }, children: [upload.fileType.startsWith("image/") ? (_jsx(Box, { sx: {
                                                width: "100%",
                                                height: "80px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                overflow: "hidden",
                                                borderRadius: "6px",
                                                minWidth: '100%',
                                            }, children: _jsx("img", { src: upload.url, alt: "Uploaded", style: {
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover",
                                                    borderRadius: "6px",
                                                } }) })) : (_jsxs(Box, { sx: { textAlign: "center" }, children: [getFileTypeIcon(upload.fileType), _jsx(Chip, { label: getFileTypeLabel(upload.fileType), size: "small", sx: {
                                                        mt: 1,
                                                        backgroundColor: "rgba(0, 171, 155, 0.1)",
                                                        color: "#00806E",
                                                        fontWeight: 500,
                                                    } })] })), _jsx(IconButton, { size: "small", onClick: () => handleRemoveFile(upload.id), sx: {
                                                position: "absolute",
                                                top: 8,
                                                right: 8,
                                                backgroundColor: "rgba(255, 255, 255, 0.9)",
                                                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                                width: 28,
                                                height: 28,
                                                "&:hover": {
                                                    backgroundColor: "rgba(255, 255, 255, 1)",
                                                    color: "#f44336",
                                                },
                                            }, children: _jsx(DeleteIcon, { sx: { fontSize: 16 } }) })] }), _jsx(Typography, { variant: "body2", sx: {
                                        mt: "auto",
                                        display: "block",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        color: "rgba(16, 29, 59, 0.8)",
                                        fontWeight: 500,
                                        textAlign: "center",
                                    }, children: upload.fileName })] })) }) }, upload.id))) })) : (_jsxs(Paper, { elevation: 0, sx: {
                    p: 4,
                    textAlign: "center",
                    backgroundColor: isDragging ? "rgba(0, 171, 155, 0.05)" : "rgba(16, 29, 59, 0.01)",
                    border: "1px dashed rgba(0, 171, 155, 0.3)",
                    borderRadius: "12px",
                    transition: "all 0.3s ease",
                    "&:hover": {
                        backgroundColor: "rgba(0, 171, 155, 0.03)",
                        boxShadow: "0 0 0 1px rgba(0, 171, 155, 0.1)",
                    },
                }, children: [_jsx(AttachFileIcon, { sx: { fontSize: 48, color: "#00AB9B", mb: 1.5, opacity: 0.8 } }), _jsx(Typography, { variant: "h6", color: "#00806E", sx: { fontWeight: 500 }, children: "Drag and drop files here" }), _jsx(Typography, { color: "textSecondary", sx: { mt: 0.5 }, children: "or click the button below to browse" }), _jsx(Typography, { variant: "caption", color: "textSecondary", sx: { display: "block", mt: 1.5 }, children: "Maximum file size: 10MB" }), _jsx(Typography, { variant: "caption", color: "textSecondary", sx: { display: "block", mt: 0.5 }, children: "Accepted formats: Images, Videos, PDF, Word, Excel, PowerPoint, TXT, CSV" }), _jsx(Box, { sx: { mt: 3 }, children: _jsxs("label", { htmlFor: "upload-file", children: [_jsx(Input, { id: "upload-file", multiple: true, type: "file", accept: acceptedFileTypes, onChange: handleFileSelect, ref: fileInputRef }), _jsx(Button, { variant: "contained", component: "span", startIcon: _jsx(CloudUploadIcon, {}), sx: {
                                        backgroundColor: "#00AB9B",
                                        px: 3,
                                        py: 1,
                                        borderRadius: "8px",
                                        boxShadow: "0 2px 8px rgba(0, 171, 155, 0.25)",
                                        "&:hover": {
                                            backgroundColor: "#008F85",
                                            boxShadow: "0 4px 12px rgba(0, 171, 155, 0.35)",
                                        },
                                    }, children: "Upload Files" })] }) })] })), uploads.length > 0 && (_jsxs(Box, { sx: { mt: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }, children: [_jsxs("label", { htmlFor: "upload-more-files", children: [_jsx(Input, { id: "upload-more-files", multiple: true, type: "file", accept: acceptedFileTypes, onChange: handleFileSelect }), _jsx(Button, { variant: "contained", component: "span", startIcon: _jsx(CloudUploadIcon, {}), sx: {
                                    backgroundColor: "#00AB9B",
                                    borderRadius: "8px",
                                    boxShadow: "0 2px 8px rgba(0, 171, 155, 0.25)",
                                    "&:hover": {
                                        backgroundColor: "#008F85",
                                        boxShadow: "0 4px 12px rgba(0, 171, 155, 0.35)",
                                    },
                                }, children: "Add More Files" })] }), _jsxs(Typography, { variant: "body2", sx: {
                            color: uploads.length >= maxFiles ? "#f44336" : "rgba(16, 29, 59, 0.6)",
                            fontWeight: 500,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                        }, children: [_jsx(Box, { component: "span", sx: {
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 28,
                                    height: 28,
                                    borderRadius: "50%",
                                    backgroundColor: uploads.length >= maxFiles ? "rgba(244, 67, 54, 0.1)" : "rgba(0, 171, 155, 0.1)",
                                    color: uploads.length >= maxFiles ? "#f44336" : "#00AB9B",
                                    fontWeight: 600,
                                }, children: uploads.length }), "/ ", maxFiles, " files"] })] })), isDragging && uploads.length > 0 && (_jsxs(Box, { sx: {
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    textAlign: "center",
                    zIndex: 2,
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    p: 3,
                    borderRadius: "12px",
                    boxShadow: "0 8px 20px rgba(0, 0, 0, 0.15)",
                    border: "2px dashed rgba(0, 171, 155, 0.5)",
                    minWidth: "200px",
                }, children: [_jsx(AttachFileIcon, { sx: { fontSize: 48, color: "#00AB9B", mb: 1 } }), _jsx(Typography, { variant: "h6", color: "#00806E", sx: { fontWeight: 500 }, children: "Drop files to upload" })] }))] }));
};
export default SharedFileAttachmentUploader;
